// TODO: feature/templates - review this file
/*
 * ---------------------------------------------------------------------------
 * Responsibility: orchestrate **all** network operations required to send a
 * batch of payment requests while exposing a simple, testable API.
 *
 * Key features
 * ------------
 * • Creates the batch on the server once (createBatch).
 * • Iterates through the prepared request rows.
 * • Skips rows that are not eligible according to evaluateStatus().
 * • Sends requests in parallel with a configurable concurrency limit.
 * • Emits progress via a callback so the UI can stay responsive without
 *   scheduling a React render for every single row.
 * • Returns a result object: { updatedRows, totals }.
 * ---------------------------------------------------------------------------
 */

import { createBatch, createPaymentRequest } from "@api";
import { replaceTokens, formatAmount, invoicesRequestedTokenMap } from "@utils";
import {
  evaluateStatus,
  pMap,
  buildTokensForRow,
  getCurrentInvoicesRequestedTotal,
} from "./utilities";

const applyTokensDeep = (tokens, value) => {
  if (typeof value === "string") return replaceTokens(tokens, value);
  if (Array.isArray(value)) return value.map((v) => applyTokensDeep(tokens, v));
  if (value && typeof value === "object") {
    const result = {};
    for (const [k, v] of Object.entries(value)) {
      result[k] = applyTokensDeep(tokens, v);
    }
    return result;
  }
  return value;
};

export const sendBatch = async ({
  rows,
  strategy,
  meta,
  paymentSettings,
  concurrency = 5,
  onProgress = () => {},
}) => {
  const batch = await createBatch(meta.batchName || null);

  let succeededAmount = 0;
  let succeededCount = 0;
  let failedCount = 0;
  let smsSent = 0;
  let emailSent = 0;

  const total = rows.length;
  let done = 0;

  const handleRow = async (row) => {
    const status = evaluateStatus(row, strategy);

    if (
      ["amountTooSmall", "noContactDetails", "noPhone", "noEmail"].includes(
        status,
      )
    ) {
      failedCount += 1;
      done += 1;
      onProgress(done, total);
      return { row: { ...row, status: "notSent" }, ok: false, error: status };
    }

    const phone = strategy.sms && !row.phoneError ? row.phone : null;
    const email = strategy.email && !row.emailError ? row.email : null;

    const displayAmount = meta.amountRequested
      ? formatAmount(meta.amountRequested)
      : meta.invoicesRequested
        ? formatAmount(
            getCurrentInvoicesRequestedTotal(row, meta.invoicesRequested),
          )
        : formatAmount(0);

    const tokens = buildTokensForRow(
      row,
      displayAmount,
      invoicesRequestedTokenMap[meta.invoicesRequested] ?? "Custom amount",
    );

    const emailContentFormat = meta.emailContent?.format ?? "HtmlSingleBlock";
    const emailBody =
      emailContentFormat === "StructuredV1"
        ? JSON.stringify(applyTokensDeep(tokens, meta.emailContent))
        : replaceTokens(tokens, meta.emailBody);

    try {
      await createPaymentRequest({
        amountRequested: meta.amountRequested
          ? parseFloat(meta.amountRequested) || 0
          : meta.invoicesRequested === "None"
            ? row.balance
            : row.minimumAmount,
        invoicesRequested: meta.invoicesRequested,
        allowAdditionalInvoices: meta.allowAdditionalInvoices,
        invoicesRequestedAreMandatory: meta.invoicesRequestedAreMandatory,
        includePdfStatement: meta.includePdfStatement,
        phone,
        email,
        communicationMethod:
          phone && email ? "SmsEmail" : phone ? "Sms" : "Email",
        reference: row.reference,
        customerName: row.customerName,
        customerId: row.customerId,
        subject: replaceTokens(tokens, meta.subject),
        message: replaceTokens(tokens, meta.smsMessage),
        emailBody: emailBody,
        payerBaseUrl:
          window.location.origin.replace("portal", "webpay") + "/pay",
        allowCardPayment:
          meta.allowCardPayment ??
          paymentSettings?.paymentRequests?.allowCard,
        allowKlarnaPayment:
          paymentSettings?.paymentRequests?.allowKlarna ?? false,
        messageTemplateId: meta.templateId ?? null,
        messageTemplateName: meta.messageTemplateName,
        batchId: batch.id,
        sendAsPlainText: meta.sendAsPlainText,
        emailContentFormat: emailContentFormat,
        paymentProfileId: meta.paymentProfileId ?? row.paymentProfileId ?? null,
        paymentRequestTemplateId: meta.templateId ?? null,
      });

      succeededAmount += row.amount || row.minimumAmount;
      succeededCount += 1;
      if (phone) smsSent += 1;
      if (email) emailSent += 1;

      done += 1;
      onProgress(done, total);

      return { row: { ...row, status: "done" }, ok: true };
    } catch (err) {
      failedCount += 1;
      done += 1;
      onProgress(done, total);

      return {
        row: { ...row, status: "sendingFailed" },
        ok: false,
        error: err?.message || "system error",
      };
    }
  };

  // 3. Run all rows with concurrency control.
  const results = await pMap(rows, concurrency, handleRow);

  const updatedRows = results.map((r) => r.row);

  return {
    updatedRows,
    totals: {
      succeeded: succeededCount,
      failed: failedCount,
      amount: succeededAmount,
      sms: smsSent,
      email: emailSent,
    },
  };
};
